import { useConfirm } from "@/components/DialogProvider";
import {
  AgenciesIcon,
  CustomersIcon,
  HomeIcon,
  ProjectsIcon,
  SettingsIcon,
  TimeTrackingIcon,
  WorkersIcon,
} from "@/components/icons/SidebarIcons";
import InstallPrompt from "@/components/InstallPrompt";
import { NavUser } from "@/components/nav-user";
import { Avatar } from "@/components/shadcn-custom/ShadcnAvatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useCourierContext } from "@/contexts/CourierContext";
import { useAdminChats, useWorkerSmsChats } from "@/features/chat";
import { useTasks } from "@/features/tasks/hooks/useTasks";
import { useAllWorkerMessages } from "@/features/workers/hooks/useWorkerMessages";
import { isDateBeforeTodayEST, isDateTodayEST } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { CourierNotificationType } from "@shared/types";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
  useRouter,
} from "@tanstack/react-router";
import type { InboxMessage } from "@trycourier/courier-js";
import { CourierInbox, CourierToast } from "@trycourier/courier-react";
import {
  Bell,
  ChevronRight,
  Clock,
  ListTodo,
  LogOut,
  type LucideIcon,
  MessageCircle,
  MessageSquare,
  Search,
  Settings2Icon,
  Trash2,
  TriangleAlert,
  User as UserIcon,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminCommandProvider, useAdminCommand } from "./-admin-command-view";
import { AdminProvider } from "./-admin-context";

export const Route = createFileRoute("/dashboard/admin")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (!context.auth.loading && !context.auth.profileLoading) {
      if (
        !context.auth.user ||
        (context.auth.userProfile?.type !== "admin" &&
          context.auth.userProfile?.type !== "manager" &&
          context.auth.userProfile?.type !== "support")
      ) {
        throw redirect({
          to: "/login",
        });
      }
    }
  },
});

function RouteComponent() {
  return (
    <AdminProvider>
      <AdminCommandProvider>
        <RouteContent />
      </AdminCommandProvider>
    </AdminProvider>
  );
}

function RouteContent() {
  const { userProfile, user, logout } = useAuth();
  const { unreadCount } = useCourierContext();
  const confirm = useConfirm();
  const router = useRouter();
  const { setOpen: openCommandView } = useAdminCommand();
  const [screenSize, setScreenSize] = useState<
    "mobile" | "tablet" | "desktop-small" | "desktop-large"
  >("desktop-large");
  const [sidebarOpen, setSidebarOpen] = useState<boolean | undefined>(undefined);
  const [isInboxOpen, setIsInboxOpen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      let newSize: "mobile" | "tablet" | "desktop-small" | "desktop-large";

      if (width < 390) {
        newSize = "mobile";
      } else if (width < 744) {
        newSize = "tablet";
      } else if (width < 1024) {
        newSize = "desktop-small";
      } else {
        newSize = "desktop-large";
      }

      setScreenSize(newSize);

      // Set sidebar state based on screen size
      if (newSize === "mobile" || newSize === "tablet") {
        setSidebarOpen(false); // Hidden
      } else if (newSize === "desktop-small") {
        setSidebarOpen(false); // Collapsed (icon-only)
      } else {
        setSidebarOpen(true); // Fully open
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  type NotificationData = {
    type?: CourierNotificationType;
    projectId?: string;
    workerId?: string;
    chatId?: string;
  };

  const handleNotificationClick = (message: InboxMessage) => {
    const data: NotificationData = (message.data as NotificationData) ?? {};
    const notificationType = data.type;

    if (!notificationType) {
      console.warn("Notification clicked without type", data);
      return;
    }

    // Exhaustive type checking - TypeScript will error if we miss a case
    switch (notificationType) {
      case CourierNotificationType.PROJECT_ASSIGNED: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}` });
        }
        break;
      }

      case CourierNotificationType.NOTE_ADDED: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}/notes` });
        }
        break;
      }

      case CourierNotificationType.TIME_ENTRY_APPROVED:
      case CourierNotificationType.TIME_ENTRY_UNAPPROVED: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}/hours` });
        }
        break;
      }

      case CourierNotificationType.PROJECT_STATUS_CHANGED: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}` });
        }
        break;
      }

      case CourierNotificationType.USER_MENTIONED_IN_NOTE: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}/notes` });
        }
        break;
      }

      case CourierNotificationType.WORKER_MISSED_CLOCK_IN: {
        const workerId = data.workerId;
        if (workerId) {
          router.navigate({ to: `/dashboard/admin/workers/${workerId}` });
        }
        break;
      }

      case CourierNotificationType.AGENCY_MANUAL_TIME_ENTRY:
      case CourierNotificationType.AGENCY_NOTE_ON_TIME_ENTRY: {
        const projectId = data.projectId;
        if (projectId) {
          router.navigate({ to: `/dashboard/admin/projects/${projectId}/hours` });
        }
        break;
      }

      case CourierNotificationType.TASK_ASSIGNED:
      case CourierNotificationType.TASK_DUE_TOMORROW:
      case CourierNotificationType.TASK_DUE_TODAY:
      case CourierNotificationType.TASK_OVERDUE: {
        router.navigate({ to: "/dashboard/admin/tasks" });
        break;
      }

      case CourierNotificationType.CHAT_MESSAGE_RECEIVED: {
        const chatId = data.chatId;
        if (chatId) {
          router.navigate({ to: `/dashboard/admin/chat/${chatId}` });
        }
        break;
      }

      case CourierNotificationType.USER_MENTIONED_IN_CHAT: {
        const chatId = data.chatId;
        if (chatId) {
          router.navigate({ to: `/dashboard/admin/chat/${chatId}` });
        }
        break;
      }

      default: {
        // This will cause a TypeScript error if we miss any enum values
        const exhaustiveCheck: never = notificationType;
        console.warn("Unhandled notification type:", exhaustiveCheck);
      }
    }
  };

  const handleLogout = async () => {
    const confirmation = await confirm({
      title: "Are you sure you want to logout?",
      description: "This will log you out of your account and you will need to log in again.",
      confirmText: "Logout",
      cancelText: "Cancel",
    });
    if (!confirmation) return;

    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Show error if access is revoked or account is deleted
  if (userProfile?.accessRevoked || userProfile?.isDeleted === true) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="mb-6 text-lg text-gray-600">
            Your account access has been {userProfile?.isDeleted ? "deactivated" : "revoked"}.
            Please contact your administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  if (sidebarOpen === undefined) {
    return null; // Prevent flash
  }

  return (
    <SidebarProvider defaultOpen={sidebarOpen}>
      <CourierToast
        autoDismiss={true}
        autoDismissTimeoutMs={5000}
        onToastItemClick={({ message }) => {
          // Reuse the same navigation logic as inbox clicks
          handleNotificationClick(message);
        }}
      />
      <div className="flex h-screen w-full flex-col">
        {/* Fixed Alert Banner + Header Container */}
        <div className="fixed top-0 z-50 w-full">
          {/* Payment Warning Alert */}
          <Alert variant="warning" className="rounded-none border-x-0 border-t-0">
            <TriangleAlert className="size-4" />
            <AlertTitle>Your account currently has a pending balance.</AlertTitle>
            <AlertDescription>
              To ensure uninterrupted access to the CRM, please complete the payment
              by 1st Friday 3pm EST. After this time, system access may be
              temporarily limited until the balance is cleared. If you need any
              assistance, feel free to contact us.
            </AlertDescription>
          </Alert>
          {/* Fixed Header - Responsive */}
          <header className="flex h-16 w-full items-center border-b border-gray-200 bg-white px-4 md:px-6">
          <div className="flex w-full items-center justify-between">
            {/* Mobile & Tablet: Hamburger menu */}
            {(screenSize === "mobile" || screenSize === "tablet") && (
              <div className="flex items-center gap-3">
                <SidebarTrigger className="-ml-1 h-9 w-9" />
              </div>
            )}

            {/* Logo - Left aligned, different sizes */}
            <Link to="/dashboard/admin" className="flex-shrink-0 flex flex-col items-start">
              <img
                src="/mainLogo.svg"
                alt="Handle"
                className={
                  screenSize === "mobile"
                    ? "h-5"
                    : screenSize === "tablet"
                      ? "h-[22px]"
                      : screenSize === "desktop-small"
                        ? "h-6"
                        : "h-7"
                }
              />
              <span className="text-[9px] text-gray-400 leading-none mt-0.5 tracking-wide">
                v{APP_VERSION}
              </span>
            </Link>

            {/* Right side - Search field/icon + Avatar */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Desktop Small & Large: Full search bar */}
              {(screenSize === "desktop-small" || screenSize === "desktop-large") && (
                <div className="relative">
                  <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search"
                    onClick={() => openCommandView(true)}
                    className="h-9 w-64 cursor-pointer rounded-md border border-gray-300 bg-white pr-3 pl-9 text-sm outline-none focus:border-[#7C12C1] focus:ring-1 focus:ring-[#7C12C1]"
                  />
                </div>
              )}

              {/* Mobile & Tablet: Search icon button */}
              {(screenSize === "mobile" || screenSize === "tablet") && (
                <button
                  onClick={() => openCommandView(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-100"
                >
                  <Search className="h-5 w-5 text-gray-600" />
                </button>
              )}

              <Dialog open={isInboxOpen} onOpenChange={setIsInboxOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon" className="relative">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 ? (
                      <Badge className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center bg-red-600 px-1 hover:bg-red-700">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Badge>
                    ) : null}
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="w-[95vw] max-w-3xl border-0 bg-transparent p-0 shadow-none"
                  showCloseButton={false}
                >
                  <div className="h-[480px] overflow-hidden rounded-md border bg-white">
                    <CourierInbox
                      height="100%"
                      onMessageClick={(props) => {
                        handleNotificationClick(props.message);
                        setIsInboxOpen(false);
                      }}
                    />
                  </div>
                </DialogContent>
              </Dialog>

              {/* User Avatar with Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full ring-offset-2 outline-none focus:ring-2 focus:ring-[#7C12C1]">
                    <Avatar
                      src={userProfile?.profileImageUrl ?? user?.photoURL ?? undefined}
                      alt={userProfile?.name ?? ""}
                      initials={userProfile?.name?.substring(0, 2)?.toUpperCase() || "PJ"}
                      className="h-9 w-9 shrink-0"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      {userProfile?.name && <p className="font-medium">{userProfile.name}</p>}
                      {user?.email && (
                        <p className="text-muted-foreground w-[200px] truncate text-sm">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/profile" className="cursor-pointer">
                      <UserIcon className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex min-w-0 flex-1 bg-[#F1F5F9] pt-[140px]">
          <AppSidebar />
          <SidebarInset className="min-w-0 flex-1 bg-[#F1F5F9]">
            <div className="flex min-w-0 flex-1 flex-col p-5">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <Outlet />
                <InstallPrompt />
              </div>
            </div>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { totalUnreadCount: workerMessagesUnread } = useAllWorkerMessages();
  const { userProfile } = useAuth();
  const { totalUnreadCount: adminChatUnread } = useAdminChats(userProfile?.uid);
  const { totalUnreadCount: workerSmsUnread } = useWorkerSmsChats(userProfile?.uid);
  const { tasks } = useTasks({ assigneeId: userProfile?.uid });

  // Combined worker communications unread (WorkerMessages + worker-sms from PortalChats)
  const totalWorkerUnread = workerMessagesUnread + workerSmsUnread;

  // Calculate overdue and due today tasks
  const { overdueTasks, dueTodayTasks } = useMemo(() => {
    const overdue = tasks.filter((task) => {
      if (!task.dueDate || task.status === "done") return false;
      return isDateBeforeTodayEST(task.dueDate);
    });

    const dueToday = tasks.filter((task) => {
      if (!task.dueDate || task.status === "done") return false;
      return isDateTodayEST(task.dueDate);
    });

    return {
      overdueTasks: overdue.length,
      dueTodayTasks: dueToday.length,
    };
  }, [tasks]);

  const data = {
    navMain: [
      {
        title: "Home",
        url: "/dashboard/admin",
        icon: HomeIcon,
      },
      {
        title: "Time Tracking",
        url: "/dashboard/admin/time_tracking",
        icon: TimeTrackingIcon,
      },
      {
        title: "Projects",
        url: "/dashboard/admin/projects",
        icon: ProjectsIcon,
      },
      {
        title: "Customers",
        url: "/dashboard/admin/customers",
        icon: CustomersIcon,
      },
      {
        title: "Workers",
        url: "/dashboard/admin/workers",
        icon: WorkersIcon,
      },
      {
        title: "Agencies",
        url: "/dashboard/admin/agencies",
        icon: AgenciesIcon,
      },
      {
        title: "Portal Chat",
        url: "/dashboard/admin/chat",
        icon: MessageSquare,
        badgeCount: adminChatUnread,
      },
      {
        title: "Worker Messages",
        url: "/dashboard/admin/worker_messages",
        icon: MessageCircle,
        badgeCount: totalWorkerUnread,
      },
      {
        title: "Tasks",
        url: "/dashboard/admin/tasks",
        icon: ListTodo,
        overdueBadgeCount: overdueTasks,
        dueTodayBadgeCount: dueTodayTasks,
      },
      {
        title: "Settings",
        url: "/dashboard/admin",
        icon: SettingsIcon,
        isActive: false,
        items: [
          {
            title: "Users",
            url: "/dashboard/admin/users",
            icon: Users,
          },
          {
            title: "Config",
            url: "/dashboard/admin/config",
            icon: Settings2Icon,
          },
          {
            title: "Event Logs",
            url: "/dashboard/admin/event_logs",
            icon: Clock,
          },
          {
            title: "SMS History",
            url: "/dashboard/admin/sms_history",
            icon: MessageCircle,
          },
          {
            title: "Trash",
            url: "/dashboard/admin/trash",
            icon: Trash2,
          },
        ],
      },
    ],
  };

  return (
    <Sidebar
      collapsible="icon"
      variant="sidebar"
      {...props}
      className="top-16 border-t-0 !bg-white [&_[data-slot=sidebar-inner]]:!bg-white [&_[data-slot=sidebar]]:!bg-white"
    >
      <SidebarContent className="gap-2 !bg-white pt-4 pl-2">
        <SidebarGroup className="py-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarTrigger className="w-full justify-start">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                <span className="ml-2">Toggle</span>
              </SidebarTrigger>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter className="!bg-white">
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

type IconComponent = LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>;

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: IconComponent;
    isActive?: boolean;
    badgeCount?: number;
    overdueBadgeCount?: number;
    dueTodayBadgeCount?: number;
    items?: {
      title: string;
      url: string;
      icon?: IconComponent;
      badgeCount?: number;
    }[];
  }[];
}) {
  const matchRoute = useMatchRoute();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => {
          const isItemActive = matchRoute({ to: item.url, fuzzy: item.items ? true : false });
          const hasActiveSubItem = item.items?.some((subItem) => matchRoute({ to: subItem.url }));
          const shouldShowSeparator = item.title === "Time Tracking" || item.title === "Agencies";

          return (
            <div key={item.title}>
              {item.items ? (
                <Collapsible asChild defaultOpen={hasActiveSubItem} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items.map((subItem) => {
                          const isSubItemActive = matchRoute({ to: subItem.url });
                          return (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton asChild isActive={!!isSubItemActive}>
                                <Link preload="viewport" to={subItem.url} onClick={handleLinkClick}>
                                  {subItem.icon && <subItem.icon />}
                                  <span>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton tooltip={item.title} asChild isActive={!!isItemActive}>
                    <Link to={item.url} preload="viewport" onClick={handleLinkClick}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <div className="ml-auto flex gap-1">
                        {item.overdueBadgeCount && item.overdueBadgeCount > 0 ? (
                          <Badge className="bg-red-600 hover:bg-red-700">
                            {item.overdueBadgeCount}
                          </Badge>
                        ) : null}
                        {item.dueTodayBadgeCount && item.dueTodayBadgeCount > 0 ? (
                          <Badge className="bg-blue-600 hover:bg-blue-700">
                            {item.dueTodayBadgeCount}
                          </Badge>
                        ) : null}
                        {item.badgeCount && item.badgeCount > 0 ? (
                          <Badge className="bg-red-600 hover:bg-red-700">{item.badgeCount}</Badge>
                        ) : null}
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {shouldShowSeparator && <Separator className="my-2" />}
            </div>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
